import { useId, useState, type ReactNode } from 'react';
import { Card, SectionLabel, ViewTab, ViewTabList, cn } from '@wundo/ui';
import { debriefTookPart } from '@wundo/shared/debriefTookPart.ts';
import type { RoleplayDebrief } from '@wundo/shared/roleplay.ts';
import { PracticeDebrief } from './PracticeDebrief.tsx';
import { PracticeTranscript } from './PracticeTranscript.tsx';
import { m } from '@/paraglide/messages.js';

type PracticeReportView = 'feedback' | 'transcript';

export interface PracticeReportProps {
  /** The attempt the feedback is about, whose conversation the transcript reads. */
  sessionId: string;
  personaName: string;
  debrief: RoleplayDebrief;
  /** Extra classes on the panel under the view switch, for the frame's own padding. */
  className?: string;
  /**
   * Takes the height its host gives it, with the open view scrolling inside,
   * for a host that does not scroll itself. Otherwise it is as tall as the
   * feedback and the host scrolls.
   */
  fill?: boolean;
  /** The ways on when the user did not take part, for a host that has them. */
  notTakenPartActions?: ReactNode;
}

/**
 * One attempt's feedback, and the conversation it was written about.
 *
 * The transcript takes exactly the room the feedback takes and scrolls inside
 * it: the feedback stays laid out underneath, invisible, so the two are the
 * same height at every width without anything being measured. The view chosen
 * stays chosen when another attempt is picked.
 */
export function PracticeReport({
  sessionId,
  personaName,
  debrief,
  className,
  fill = false,
  notTakenPartActions,
}: PracticeReportProps) {
  const [view, setView] = useState<PracticeReportView>('feedback');
  const id = useId();
  const reading = view === 'transcript';

  return (
    <>
      <div className='flex shrink-0 flex-wrap items-center gap-2 border-b border-line px-6.5 py-4'>
        <SectionLabel id={`${id}-view`}>{m.practice_report_view()}</SectionLabel>
        <ViewTabList aria-labelledby={`${id}-view`}>
          <ViewTab
            id={`${id}-feedback-tab`}
            active={!reading}
            onClick={() => setView('feedback')}
          >
            {m.practice_report_feedback()}
          </ViewTab>
          <ViewTab
            id={`${id}-transcript-tab`}
            active={reading}
            onClick={() => setView('transcript')}
          >
            {m.practice_report_transcript()}
          </ViewTab>
        </ViewTabList>
      </div>

      <div
        className={cn('px-6.5 pt-5.5', fill && 'flex min-h-0 flex-1 flex-col', className)}
      >
        <div className={cn('relative', fill && 'min-h-0 flex-1')}>
          <div
            role='tabpanel'
            aria-labelledby={`${id}-feedback-tab`}
            className={cn(
              'flex flex-col gap-4',
              fill && 'h-full overflow-y-auto',
              fill && !debriefTookPart(debrief) && 'justify-center',
              reading && 'invisible',
            )}
          >
            <PracticeDebrief
              debrief={debrief}
              notTakenPartActions={notTakenPartActions}
            />
          </div>

          {reading && (
            <Card
              role='tabpanel'
              aria-labelledby={`${id}-transcript-tab`}
              padding='none'
              className='absolute inset-0 flex flex-col'
            >
              <div className='min-h-0 flex-1 overflow-y-auto p-5.5'>
                <PracticeTranscript
                  key={sessionId}
                  sessionId={sessionId}
                  personaName={personaName}
                  remarks={debrief.remarks}
                />
              </div>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
